/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * **The one walk over an expression, and where each node sits in it** (`4c`, `#1334`).
 *
 * An expression is a tree: a case ladder whose rungs hold conditions and answers, an answer that is
 * itself a decision, a calculation whose operands are calculations. Two readers already walked it —
 * `rootsRead` (*what does this read*) and `sharedAcrossFanOutOn` (*does it address the library*) —
 * through an **untyped** walk that visited every object in the document. `#1334` needed a third
 * question, *is anything in here unfinished*, and that one has to say **where**: a finding three
 * levels into an expression is useless without an address the editor can open to (pattern 38).
 *
 * So the walk gained locations, and it had to become typed to do it, because a location is only
 * worth having if it is the one the **screen** uses. That is the design:
 *
 * - **The location is in the editor's terms, not the document's.** A ladder is stored as a
 *   right-nested `else` chain and shown as *Case 1, Case 2, … Otherwise*; a condition list is a
 *   `CriteriaNode` shown as a flat list. The walk reads both through the **same projections the
 *   editor renders from** — {@link flattenCaseLadder} and {@link flattenPredicate} — so a location
 *   and the anchor the editor stamps at that place agree by construction rather than by two
 *   functions happening to count the same way.
 * - **It visits only what the engine reads.** A producer's kept operands, and the value a
 *   suppression carries so it can be restored, are transported and never resolved
 *   (`macro-expression.engine.ts`). The untyped walk counted them; a finding about either would be
 *   a warning about a value nothing uses.
 * - **It does not enter a reference.** The addressed expression is not in this tree — the console
 *   keeps the address and only `getForRuntime` substitutes it (`X5`) — and a finding inside it is its
 *   **author's**, not the consumer's (`#1328`: *"the onus is on the expression author"*).
 *
 * `mapExpressionPaths` in `mapping-value-round-trip.ts` is the **rewrite** over the same tree and
 * stays separate on purpose: it returns a new document, and a visitor that also rebuilt would be two
 * things wearing one name.
 */

import {
  conditionRhsShape,
  operationArity,
  readComputedOperands,
  readConditionOperands,
  toCriteriaNode,
  type ConditionOperator,
  type CriteriaNode,
  type MacroCondition,
  type MacroExpression,
  type ValueOperand,
  type CollectionOperand,
} from '../../../types/decorator-expression';
import { flattenCaseLadder } from './case-ladder.model';
import { flattenPredicate, type ConditionListMode } from './condition-builder.model';

/**
 * One step into an expression, named for what the editor shows there.
 *
 * Indices are **0-based** here and 1-based wherever an operator reads them ({@link describeLocation}),
 * which is the convention the editor's own labels already use (*Remove case 2*, *Remove operand 1*).
 */
export type ExpressionLocationStep =
  | { kind: 'case'; index: number }
  | { kind: 'condition'; index: number }
  | { kind: 'side'; side: 'left' | 'right' }
  | { kind: 'then' }
  | { kind: 'otherwise' }
  | { kind: 'operand'; index: number };

/** Where a node sits, from the value's own root. `[]` is the root. */
export type ExpressionLocation = readonly ExpressionLocationStep[];

/** A location as a token — what the editor stamps on the element it renders there (`data-expr-at`). */
export function encodeLocation(at: ExpressionLocation): string {
  return at
    .map((step) => {
      switch (step.kind) {
        case 'case':
        case 'condition':
        case 'operand':
          return `${step.kind}${step.index}`;
        case 'side':
          return step.side;
        default:
          return step.kind;
      }
    })
    .join('.');
}

/** The attribute a rendered location carries, so a reveal can find it inside its row. */
export const EXPRESSION_LOCATION_ATTRIBUTE = 'data-expr-at';

/**
 * A location in the words the editor shows — `Case 2 › condition 1 › Value to test`.
 *
 * `›` and not `→`: an arrow already means *resolves to* on a mapping row, and a glyph with two
 * meanings is the defect `#1269` is about. Empty for the root, which the caller names itself.
 */
export function describeLocation(at: ExpressionLocation): string {
  return at
    .map((step) => {
      switch (step.kind) {
        case 'case':
          return `Case ${step.index + 1}`;
        case 'condition':
          return `condition ${step.index + 1}`;
        case 'side':
          // The labels `ConditionRow` renders above each side.
          return step.side === 'left' ? 'Value to test' : 'Compare with';
        case 'then':
          return 'Then';
        case 'otherwise':
          return 'Otherwise';
        case 'operand':
          return `operand ${step.index + 1}`;
      }
    })
    .join(' › ');
}

/** What the walk hands a visitor. Every callback is optional; a question asks only what it needs. */
export interface ExpressionVisitor {
  /** Every value the engine reads, the root included (`at` is `[]`). */
  value?: (node: MacroExpression, at: ExpressionLocation) => void;
  /**
   * **Each member of a list, as a calculation's operand** (`CO8`, `2e`). Not a `value`: it is not an
   * expression and resolves to many, so a question about values has to decide whether it asks this
   * one too — a list's root is still a root the expression reads, and its path can still be wrong.
   */
  collection?: (operand: CollectionOperand, at: ExpressionLocation) => void;
  /**
   * One case's condition list, as the builder shows it — `null` where the builder cannot (a `not`,
   * or a group inside a group), in which case the walk still visits its conditions, addressed at the
   * case, because the engine still evaluates them.
   */
  caseConditions?: (
    list: { mode: ConditionListMode; conditions: MacroCondition[] } | null,
    at: ExpressionLocation,
  ) => void;
  /**
   * One comparison, with its operands read through `readConditionOperands` so a pre-`E2b` spelling
   * is seen as what it means.
   */
  condition?: (
    condition: {
      operator: ConditionOperator;
      left: ValueOperand | undefined;
      right: ValueOperand | undefined;
      values: (string | number | boolean)[] | undefined;
    },
    at: ExpressionLocation,
  ) => void;
}

/**
 * Walk an expression, telling the visitor what is where.
 *
 * Depth is not bounded here: the engine resolves any depth, and `MAX_CONDITIONAL_NESTING` bounds
 * what the **editor** will draw, not what a document may hold. A location beneath that bound is
 * still true; the reveal lands on the deepest part of it the editor rendered.
 */
export function walkExpression(expression: MacroExpression | undefined, visitor: ExpressionVisitor): void {
  if (expression) visit(expression, [], visitor);
}

function visit(node: MacroExpression, at: ExpressionLocation, visitor: ExpressionVisitor): void {
  visitor.value?.(node, at);
  switch (node.type) {
    case 'computed':
      // A producer's operands are kept for the way back, never read (`#1223`).
      if (operationArity(node.operation) === 'produces') return;
      readComputedOperands(node).forEach((operand, index) => {
        const operandAt: ExpressionLocation = [...at, { kind: 'operand', index }];
        // Each member of a list is not an expression, so it is handed over as what it is.
        if (operand.type === 'collection') visitor.collection?.(operand, operandAt);
        else visit(operand, operandAt, visitor);
      });
      return;
    case 'conditional': {
      const ladder = flattenCaseLadder(node);
      ladder.rungs.forEach((rung, index) => {
        const caseAt: ExpressionLocation = [...at, { kind: 'case', index }];
        visitCaseConditions(rung.condition, caseAt, visitor);
        visit(rung.then, [...caseAt, { kind: 'then' }], visitor);
      });
      if (ladder.otherwise) visit(ladder.otherwise, [...at, { kind: 'otherwise' }], visitor);
      return;
    }
    // `static`, `calculated` and `reference` hold no nested value this tree owns, and a
    // `suppress`'s carried value is not read.
    default:
      return;
  }
}

function visitCaseConditions(predicate: CriteriaNode, caseAt: ExpressionLocation, visitor: ExpressionVisitor): void {
  const list = flattenPredicate(predicate);
  visitor.caseConditions?.(list, caseAt);
  const conditions = list ? list.conditions : leafConditions(toCriteriaNode(predicate));
  conditions.forEach((condition, index) => {
    // Addressed by position only where the builder shows positions.
    const at: ExpressionLocation = list ? [...caseAt, { kind: 'condition', index }] : caseAt;
    const read = readConditionOperands(condition);
    visitor.condition?.(read, at);
    if (read.left) visit(read.left, [...at, { kind: 'side', side: 'left' }], visitor);
    if (read.right && conditionRhsShape(read.operator) !== 'none') {
      visit(read.right, [...at, { kind: 'side', side: 'right' }], visitor);
    }
  });
}

/** Every comparison in a predicate tree, in order — for the shapes the flat builder cannot draw. */
function leafConditions(node: CriteriaNode): MacroCondition[] {
  switch (node.type) {
    case 'condition':
      return [node.condition];
    case 'not':
      return leafConditions(node.of);
    default:
      return node.of.flatMap(leafConditions);
  }
}
